/**
 * Analytics Event Definitions
 *
 * All event enums and property interfaces for analytics tracking.
 * Events are organized by category for easy discovery and maintenance.
 * Matches mobile app event naming for cross-platform analysis.
 *
 * To add a new event:
 * 1. Add to the appropriate enum (or create a new category enum)
 * 2. Update the AnalyticsEventName union type if adding a new category
 * 3. Add a generator in generators.ts
 */

import type { SetupModalItem } from '@ruta/shared';

export type { SetupModalItem };

// ============================================
// EVENT CATEGORY ENUMS
// ============================================

/**
 * High-level event categories for grouping and filtering in PostHog
 */
export enum AnalyticsEventCategory {
  ONBOARDING = 'onboarding',
  SUBSCRIPTION = 'subscription',
  CUSTOMER = 'customer',
  VISIT = 'visit',
  CREW = 'crew',
  SEARCH = 'search',
  PAGE = 'page',
  REVIEW = 'review',
  INVOICE = 'invoice',
  AUTH = 'auth',
  POST_IMPORT = 'post_import',
}

// ============================================
// INDIVIDUAL EVENT ENUMS BY CATEGORY
// ============================================

/**
 * Onboarding funnel events
 */
export enum OnboardingEvent {
  STEP_VIEWED = 'admin_onboarding_step_viewed',
  STEP_COMPLETED = 'admin_onboarding_step_completed',
  ABANDONED = 'admin_onboarding_abandoned',
  COMPLETED = 'admin_onboarding_completed',
}

/**
 * Subscription/service lifecycle events
 */
export enum SubscriptionEvent {
  CREATED = 'admin_subscription_created',
  ACTIVATED = 'admin_subscription_activated',
  PAUSED = 'admin_subscription_paused',
  DEACTIVATED = 'admin_subscription_deactivated',
  RATE_SENT = 'admin_rate_sent_to_customer',
  // Note: RATE_ACCEPTED and RATE_DECLINED use shared events from @ruta/shared
  // with source: 'admin' to differentiate from customer-initiated events
}

/**
 * Customer management events
 */
export enum CustomerEvent {
  CREATED = 'admin_customer_created',
  VIEWED = 'admin_customer_viewed',
  UPDATED = 'admin_customer_updated',
  STATUS_CHANGED = 'admin_customer_status_changed',
}

/**
 * Visit events (matches mobile for cross-platform)
 */
export enum VisitEvent {
  SCHEDULED = 'admin_visit_scheduled',
  RESCHEDULED = 'admin_visit_rescheduled',
  /** Same name as the crew app — an office start belongs on the same comparison. */
  STARTED = 'visit_started',
  /** Same name as the crew app — office completions belong on the same scorecard. */
  COMPLETED = 'visit_completed',
  /** Locked name (RUT-3548) — a visit was marked priority; has_note says whether a crew note was written. */
  PRIORITY_SET = 'service_priority_set',
  /** Same name as the crew app — a skipped visit put back on the schedule. */
  SKIP_REVERTED = 'visit_skip_reverted',
}

/**
 * Crew/schedule operations events
 */
export enum CrewEvent {
  ASSIGNED = 'admin_crew_assigned',
  BULK_OPERATION = 'admin_bulk_operation_performed',
}

/**
 * Search and filter events
 */
export enum SearchEvent {
  PERFORMED = 'admin_search_performed',
  RESULT_CLICKED = 'admin_search_result_clicked',
  NO_RESULTS = 'admin_search_no_results',
  FILTER_APPLIED = 'admin_filter_applied',
  FILTER_CLEARED = 'admin_filter_cleared',
}

/**
 * Page engagement events
 */
export enum PageEvent {
  VIEWED = 'admin_page_viewed',
  ENGAGED = 'admin_page_engaged',
  EXITED = 'admin_page_exited',
}

/**
 * Review queue events
 */
export enum ReviewEvent {
  ITEM_VIEWED = 'admin_review_item_viewed',
  APPROVED = 'admin_review_approved',
  REJECTED = 'admin_review_rejected',
}

/**
 * Invoice events
 */
export enum InvoiceEvent {
  VIEWED = 'admin_invoice_viewed',
  PAYMENT_RETRIED = 'admin_invoice_payment_retried',
}

/**
 * First value moment events (activation metrics)
 */
export enum FirstValueEvent {
  FIRST_CUSTOMER_CREATED = 'admin_first_customer_created',
  FIRST_SERVICE_ACTIVATED = 'admin_first_service_activated',
  FIRST_VISIT_COMPLETED = 'admin_first_visit_completed',
  FIRST_INVOICE_SENT = 'admin_first_invoice_sent',
}

/**
 * Post-import first-run (RUT-3501). Owner success is first_week_visible.
 * visit_started stays the crew-in-the-field scorecard event — do not rename it.
 */
export enum PostImportEvent {
  FIRST_WEEK_VISIBLE = 'first_week_visible',
  SCHEDULE_EMPTY_STATE_SHOWN = 'schedule_empty_state_shown',
  SETUP_MODAL_SHOWN = 'setup_modal_shown',
  SETUP_MODAL_ITEM_CLICKED = 'setup_modal_item_clicked',
  SETUP_MODAL_DISMISSED = 'setup_modal_dismissed',
  SETUP_MODAL_COMPLETED = 'setup_modal_completed',
}

/**
 * Stranger-from-the-site funnel (RUT-3507). `setup_shop_started` fires on the
 * marketing site; this is the office-app half once an account exists.
 */
export enum AcquisitionEvent {
  REGISTRATION_COMPLETED = 'registration_completed',
}

// ============================================
// UNION TYPE FOR ALL EVENTS
// ============================================

export type AnalyticsEventName =
  | OnboardingEvent
  | SubscriptionEvent
  | CustomerEvent
  | VisitEvent
  | CrewEvent
  | SearchEvent
  | PageEvent
  | ReviewEvent
  | InvoiceEvent
  | FirstValueEvent
  | PostImportEvent
  | AcquisitionEvent;

// ============================================
// PROPERTY INTERFACES BY CATEGORY
// ============================================

/**
 * Onboarding step values with order
 */
export enum OnboardingStep {
  USER_ACCOUNT = 'user-account',
  CONFIRM = 'confirm',
  ORGANIZATION = 'organization',
  SERVICE_AREA = 'service-area',
  TEMPLATES = 'templates',
  TEAM = 'team',
  PAYMENT = 'payment',
  INVITE_SIGNUP = 'invite-signup',
}

/**
 * Step order lookup - derive order from step enum
 */
export const ONBOARDING_STEP_ORDER: Record<OnboardingStep, number> = {
  // Signup runs who's signing up → company → email code → pay (RUT-4410).
  [OnboardingStep.USER_ACCOUNT]: 1,
  [OnboardingStep.ORGANIZATION]: 2,
  [OnboardingStep.INVITE_SIGNUP]: 1,
  [OnboardingStep.CONFIRM]: 3,
  [OnboardingStep.PAYMENT]: 4,
  // No longer asked during signup; kept so old events still decode.
  [OnboardingStep.SERVICE_AREA]: 5,
  [OnboardingStep.TEMPLATES]: 6,
  [OnboardingStep.TEAM]: 7,
};

/**
 * Search/filter context values
 */
export enum SearchContext {
  CUSTOMERS = 'customers',
  SERVICES = 'services',
  GLOBAL = 'global',
  INVOICES = 'invoices',
}

/**
 * Review queue action type values (for analytics tracking)
 * Note: Named differently from the ReviewAction interface in api/types.ts
 */
export enum ReviewActionType {
  APPROVE = 'approve',
  ADJUST = 'adjust',
  DISMISS = 'dismiss',
}

export interface OnboardingProperties {
  step: OnboardingStep;
  stepOrder?: number;
  durationSeconds?: number;
  timeSpentTotal?: number;
  stepsCompleted?: number;
  validationErrors?: string[];
  source?: string;
}

export interface SubscriptionProperties {
  subscriptionId?: string;
  customerId?: string;
  templateId?: string;
  templateName?: string;
  billingMethod?: string;
  rate?: number;
  reason?: string;
  monthlyValueAtRisk?: number;
  lifetimeValue?: number;
  via?: 'email' | 'sms';
}

export interface CustomerProperties {
  customerId?: string;
  customerName?: string;
  source?: string; // CustomerSource enum value (manual, rate_request, referral, import, etc.)
  hasAddress?: boolean;
  fromPage?: string;
  fieldsChanged?: string[];
  fromStatus?: string;
  toStatus?: string;
}

export interface VisitProperties {
  visitId?: string;
  subscriptionId?: string;
  reason?: string;
  daysMoved?: number;
  crewId?: string;
  orgId?: string;
  wasOffline?: boolean;
  /** Who acted: the crew, the office, a bulk selection, or the assistant. */
  source?: 'crew' | 'office' | 'bulk' | 'assistant';
  tap_time?: string;
  tap_latitude?: number;
  tap_longitude?: number;
  tap_accuracy?: number;
  flow_variant?: string;
  crewMemberId?: string;
  visit_id?: string;
  org_id?: string;
  crew_id?: string;
  has_note?: boolean;
}

export interface CrewProperties {
  crewId?: string;
  subscriptionId?: string;
  isBulk?: boolean;
  operation?: 'assign' | 'schedule' | 'message' | 'deactivate' | 'reschedule';
  count?: number;
}

export interface SearchProperties {
  query?: string;
  context: SearchContext;
  resultsCount?: number;
  resultPosition?: number;
  resultType?: string;
  filterType?: string;
  filterValue?: string;
  queryLength?: number;
  timeToClick?: number;
}

export interface PageProperties {
  pageName: string;
  referrerPage?: string;
  timeOnPageSeconds?: number;
  scrollDepthPercent?: number;
  exitType?: 'navigation' | 'close' | 'timeout';
}

export interface ReviewProperties {
  visitId: string;
  priority?: string;
  billableAmount?: number;
  timeToApprove?: number;
  reason?: string;
  amountAtRisk?: number;
}

export interface InvoiceProperties {
  invoiceId: string;
  amount?: number;
  status?: string;
}

export interface FirstValueProperties {
  timeSinceSignup?: number;
  templateUsed?: string;
  amount?: number;
}

export interface AcquisitionProperties {
  org_id: string;
  company_name?: string;
}

export interface PostImportProperties {
  org_id?: string;
  role?: string;
  visits_shown_next_7d?: number;
  empty_active_services?: number;
  service_id?: string;
  service_type?: string;
  item?: SetupModalItem;
}

// ============================================
// USER TRAITS FOR IDENTIFICATION
// ============================================

export interface UserTraits {
  email?: string;
  fullName?: string;
  role?: string;
  orgId: string;
  organizationName?: string;
}
